// src/ApiPanel/pages/gst/GstInformation.jsx
// The head-wise position for one tax period: output tax, ITC, reversals,
// what has been paid, what is left.
//
// The disclaimer at the top is not boilerplate. This screen is computed from
// the internal invoice register, and someone will eventually try to file
// straight off it. It has to say, on the screen and not in a wiki, that it
// needs reconciling against GSTR-2B first.
import React, { useState } from "react";
import {
  Badge, Card, DataTable, MonthField, Notice, ReportPage, Stat, StatGrid,
  TextField, count, currentPeriod, money, rupees,
} from "../../components/reports/ReportKit";
import { FilterBar } from "../../components/reports/ReportKit";
import { useReportQuery } from "../../hooks/useReportQuery";
import { API_PANEL_ENDPOINTS } from "../../constants/apiPanelAuth";

export default function GstInformation() {
  const [filters, setFilters] = useState({ period: currentPeriod(), gstin: "" });
  const [applied, setApplied] = useState({ period: currentPeriod(), gstin: "" });

  const { data, loading, error } = useReportQuery(API_PANEL_ENDPOINTS.GST_INFORMATION, applied);

  const heads = data?.heads || {};
  const outward = data?.outward || {};
  const inward = data?.inward || {};
  const creditNotes = data?.credit_notes || {};
  const reversals = data?.reversals || {};
  const payments = data?.payments || {};

  const headRows = ["cgst", "sgst", "igst"]
    .filter((h) => heads[h])
    .map((h) => ({ head: h.toUpperCase(), ...heads[h] }));

  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <ReportPage
      title="GST Information"
      subtitle="Output tax, input credit and net position for a tax period, computed from the invoice register."
    >
      <Card>
        <FilterBar
          onApply={() => setApplied(filters)}
          onReset={() => {
            const fresh = { period: currentPeriod(), gstin: "" };
            setFilters(fresh);
            setApplied(fresh);
          }}
          busy={loading}
        >
          <MonthField label="Tax period" value={filters.period} onChange={set("period")} max={currentPeriod()} />
          <TextField
            label="GSTIN"
            value={filters.gstin}
            onChange={(v) => set("gstin")(v.toUpperCase())}
            placeholder="Defaults to your company GSTIN"
            maxLength={15}
          />
        </FilterBar>
      </Card>

      {error && <Notice tone="bad">{error}</Notice>}

      {data?.disclaimer && <Notice tone="warn">{data.disclaimer}</Notice>}

      <StatGrid>
        <Stat label="Outward Taxable" value={rupees(outward.taxable_value)} sub={`${count(outward.invoice_count)} invoices`} />
        <Stat label="Inward Taxable" value={rupees(inward.taxable_value)} sub={`${count(inward.invoice_count)} invoices`} />
        <Stat label="ITC Reversed" value={rupees(reversals.cgst ? sum3(reversals) : 0)} sub={`${count(reversals.count)} entries`} tone="warn" />
        <Stat label="Paid This Period" value={rupees(payments.total)} sub={`${count(payments.count)} challans`} tone="ok" />
        <Stat label="Net Payable" value={rupees(data?.net_payable)} tone="brand" />
      </StatGrid>

      <Card title="Head-wise position">
        <DataTable
          columns={[
            { key: "head", label: "Head" },
            { key: "output_tax", label: "Output Tax", numeric: true, render: (r) => money(r.output_tax) },
            { key: "input_tax_credit", label: "Input Credit", numeric: true, render: (r) => money(r.input_tax_credit) },
            { key: "itc_reversed", label: "ITC Reversed", numeric: true, render: (r) => money(r.itc_reversed) },
            {
              key: "net_liability",
              label: "Net Liability",
              numeric: true,
              render: (r) => <strong>{money(r.net_liability)}</strong>,
            },
            { key: "paid", label: "Paid", numeric: true, render: (r) => money(r.paid) },
            {
              key: "balance",
              label: "Balance",
              numeric: true,
              render: (r) => {
                const v = Number(r.balance || 0);
                if (v > 0) return <span className="apip-bad-txt"><strong>{money(r.balance)}</strong></span>;
                if (v < 0) return <span className="apip-ok-txt">{money(r.balance)}</span>;
                return <span className="apip-ok-txt">0.00</span>;
              },
            },
          ]}
          rows={headRows}
          loading={loading}
          empty="Nothing computed for this period."
          rowKey={(r) => r.head}
        />
      </Card>

      <div className="apip-two-col">
        <Card title="Documents in this period">
          <dl className="apip-kv apip-kv-2">
            <dt>Outward invoices</dt><dd className="apip-num">{count(outward.invoice_count)}</dd>
            <dt>Outward taxable</dt><dd className="apip-num">{rupees(outward.taxable_value)}</dd>
            <dt>Inward invoices</dt><dd className="apip-num">{count(inward.invoice_count)}</dd>
            <dt>Inward taxable</dt><dd className="apip-num">{rupees(inward.taxable_value)}</dd>
            <dt>Credit notes</dt><dd className="apip-num">{count(creditNotes.invoice_count)}</dd>
            <dt>Credit note value</dt><dd className="apip-num">{rupees(creditNotes.taxable_value)}</dd>
          </dl>
        </Card>

        <Card title="Filing status">
          {data?.filing_status?.length ? (
            <DataTable
              columns={[
                { key: "return_type", label: "Return" },
                { key: "status", label: "Status", render: (r) => <Badge value={r.status} /> },
                { key: "arn", label: "ARN", render: (r) => <span className="apip-mono">{r.arn || "—"}</span> },
                { key: "filed_at", label: "Filed", render: (r) => (r.filed_at ? r.filed_at.slice(0, 10) : "—") },
              ]}
              rows={data.filing_status}
              rowKey={(r, i) => `${r.return_type}-${i}`}
            />
          ) : (
            <Notice tone="info">
              No return period record for this month yet. The period is treated as open, so
              invoices can still be generated and cancelled in it.
            </Notice>
          )}
        </Card>
      </div>
    </ReportPage>
  );
}

function sum3(obj) {
  return ["cgst", "sgst", "igst"].reduce((acc, k) => acc + Number(obj[k] || 0), 0).toFixed(2);
}
